/*!
 * beatGrid.js — the beat a pattern starts on
 * © 2026 Rob Canning — GPLv3
 *
 *     synth(dur:0.25, freq:Pseq(220, 330, inf), quant:1)    on the next beat
 *     synth(..., quant:4)                                     on the next bar
 *     synth(..., quant:0.5)                                   on the next half beat
 *     synth(..., quant:0)                                     at once
 *
 * As in SuperCollider (Pbind's quant on TempoClock.default): the grid is a
 * tempo -- 60 bpm unless set, so a beat is a second, the same unit as dur:
 * -- counted from a fixed origin, and quant:N waits for the next multiple
 * of N beats. The origin is the server's clock (serverClock.js) when it is
 * known: the laptop and the projector window, and any other machine, count
 * the same beats. Before the clock has settled, this machine's own.
 *
 * A pattern with a fixed dur: also steps on the shared grid of its dur
 * (multiples of it from the same origin): it starts on the first step at
 * or after the boundary. Lined up only at their starts, two runs of
 * dur:0.12 on different bars were 40 ms apart -- a 4 s bar is 33 1/3 steps.
 *
 * tempo(120) changes the speed of the grid, on the next whole beat: the
 * beats go on counting from there at the new rate, so a bar is still four
 * of them and quant:4 still lands on one. The grid is a list of such
 * changes (tempoMap): beat b at grid time ms, bpm from then on. The live
 * console settles the beat a change starts on (tempo(120, beat:N)), so the
 * laptop and the projector window change on the same one. dur: in synth()
 * is in beats: at 60 bpm, the default, a beat is a second, as before.
 *
 * A clock from outside -- the room's timeline, Ableton Link through the
 * server, MIDI clock, OSC -- sets the grid with setTimeline({ ms, beat,
 * bpm }): from grid time ms, beat `beat`, at bpm. What is to come after it
 * goes; what came before stays as long as the beats still rise, so a
 * downbeat (beat 0 again) starts the map afresh. Running patterns are not
 * touched: they count their steps on the audio clock, and only the next
 * boundary anyone asks for moves.
 *
 * Pure but for the clock: the arithmetic (nextBoundary, beatAt, msAtBeat)
 * is testable alone.
 */

import { serverNow } from "../../system/serverClock.js";

// The tempo map: from grid time ms (beat number beat) on, bpm. Never empty;
// the first is the grid's origin: 60 bpm, so beat n is at n seconds.
const ORIGIN = { ms: 0, beat: 0, bpm: 60 };
let tempoMap = [ORIGIN];
// who set the grid last: "local" (this machine's tempo()), "room", "link", "midi", "osc"
let source = "local";

const clampBpm = b => Math.min(999, Math.max(1, b));

// keep one change before now (the one in force) and everything after
function dropLongPast(now) {
  const past = tempoMap.filter(t => t.ms <= now - 10000);
  if (past.length > 1) tempoMap = tempoMap.slice(past.length - 1);
}

const segByMs = ms => { let s = tempoMap[0]; for (const t of tempoMap) if (t.ms <= ms) s = t; return s; };
const segByBeat = b => { let s = tempoMap[0]; for (const t of tempoMap) if (t.beat <= b) s = t; return s; };

/** The beat (a number with a fraction) at grid time ms. */
export function beatAt(ms) {
  const s = segByMs(ms);
  return s.beat + ((ms - s.ms) * s.bpm) / 60000;
}

/** The grid time (ms) of beat b. */
export function msAtBeat(b) {
  const s = segByBeat(b);
  return s.ms + ((b - s.beat) * 60000) / s.bpm;
}

/** The tempo at grid time ms (default: now). */
export function gridBpm(ms = gridNow().ms) {
  return segByMs(ms).bpm;
}

/** The first whole beat at least leadMs from now: where a tempo change starts. */
export function nextWholeBeat(leadMs = 50) {
  return Math.ceil(beatAt(gridNow().ms + leadMs) - 1e-9);
}

/**
 * From beat `beat` on, `bpm`. A change still to come is replaced by this
 * one; changes long past are dropped.
 */
export function setTempo(bpm, beat = nextWholeBeat()) {
  const b = Number(bpm);
  if (!(b > 0)) return null;
  const at = Number(beat);
  const ms = msAtBeat(at);
  tempoMap = tempoMap.filter(t => t.beat < at);
  dropLongPast(gridNow().ms);
  tempoMap.push({ ms, beat: at, bpm: clampBpm(b) });
  source = "local";
  return { bpm: b, beat: at, ms };
}

/**
 * The grid from a clock outside: from grid time ms, beat `beat`, at bpm.
 * Everything after ms goes, and so does anything before it with a beat at
 * or past `beat` (a downbeat counts from 0 again: the beats must rise for
 * msAtBeat to find them). from: who set it, for clock() to say.
 * Returns the entry, or null for numbers that make no grid.
 */
export function setTimeline({ ms, beat, bpm } = {}, from = "room") {
  const m = Number(ms), b = Number(beat), t = Number(bpm);
  if (!Number.isFinite(m) || !Number.isFinite(b) || !(t > 0)) return null;
  const entry = { ms: m, beat: b, bpm: clampBpm(t) };
  tempoMap = tempoMap.filter(e => e.ms < m && e.beat < b);
  tempoMap.push(entry);
  dropLongPast(gridNow().ms);
  source = String(from);
  return entry;
}

/** Where the grid is now: { ms, beat, bpm, source }, as setTimeline takes it. */
export function gridTimeline(ms = gridNow().ms) {
  return { ms, beat: beatAt(ms), bpm: gridBpm(ms), source };
}

/** The first whole beat at or after grid time ms. */
export function wholeBeatAfter(ms) {
  return Math.ceil(beatAt(ms) - 1e-9);
}

/** Who set the grid last: local, room, link, midi or osc. */
export const gridSource = () => source;

/** Back to 60 bpm from the origin (a new set, tests). */
export function resetTempo() { tempoMap = [ORIGIN]; source = "local"; }

/**
 * Grid time of the first multiple of quantBeats beats at or after grid time
 * t (+ minLeadMs), on the tempo map -- and, given stepBeats, of the first
 * multiple of the step at or after that.
 */
export function nextGridBoundary(t, quantBeats, minLeadMs = 0, stepBeats = 0, phaseBeats = 0) {
  if (!(quantBeats > 0)) return t;
  const b = beatAt(t + minLeadMs);
  // the next multiple of quant, phase beats on (quant:[2, 1]: the 2nd and 4th beats)
  const ph = Number(phaseBeats) || 0;
  let target = Math.ceil((b - ph) / quantBeats - 1e-9) * quantBeats + ph;
  // on its step grid too -- that grid shifted by the same phase, or it would undo it
  if (stepBeats > 0) target = Math.ceil((target - ph) / stepBeats - 1e-9) * stepBeats + ph;
  return msAtBeat(target);
}

/** Seconds from grid time ms to `beats` beats later, across tempo changes. */
export function beatsToSec(ms, beats) {
  return (msAtBeat(beatAt(ms) + beats) - ms) / 1000;
}

/**
 * The first multiple of `quantBeats` beats at or after `t` (ms, on a clock
 * whose beat 0 is at 0), never sooner than `minLeadMs` ahead.
 */
export function nextBoundary(t, quantBeats, bpm = 60, minLeadMs = 0, stepMs = 0) {
  const q = (60000 / bpm) * quantBeats;
  if (!(q > 0)) return t;
  const b = Math.ceil((t + minLeadMs) / q) * q;
  // on the step grid too: the first multiple of stepMs at or after the boundary
  return stepMs > 0 ? Math.ceil(b / stepMs - 1e-9) * stepMs : b;
}

/**
 * The grid's clock at local time `perf`: the server's (shared by every
 * machine) when known, else this one's. { ms, domain }.
 */
export function gridNow(perf = performance.now()) {
  const server = serverNow(perf);
  return server != null ? { ms: server, domain: "server" } : { ms: perf, domain: "local" };
}

// The beat now, for a project's extension module or anything else on the
// page that wants to move with it: { beat, bpm, source }
if (typeof window !== "undefined") {
  window.oscillaBeat = () => { const { ms } = gridNow(); return { beat: beatAt(ms), bpm: gridBpm(ms), source }; };
}

